import ThreeColumnLayout from "@/components/common/three-column-layout";
import FullModal from "@/workflow/components/full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
// import { useState } from "react";
import { Content } from "../../if";
import type {
  Conditions,
  Input,
  NodeData,
} from "@/workflow_api/types/workflow";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { ConditionProvider } from "../../if/useCondition";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { Icon } from "@elaniintc/goes-design-system";
import VariableInput from "./../variable-input";
import { FormProvider, useForm } from "react-hook-form";
import {
  useExecuteNodeInSandbox,
  useGetNodeTestInputExecution,
  useUpdateNodeTestInput,
} from "@/workflow_api/services/nodes.sandbox.api";
import Output from "./../output";
import type { Node } from "@xyflow/react";

export type FormGroup = {
  form_node_id: string;
  form_id: string;
  form_name: string;
  fields: Map<
    string,
    {
      field: {
        id: string;
        name: string;
        type: string;
        label?: string;
        form_step_id?: string;
        form_section_id?: string;
      };
      step_id: string;
      step_name: string;
      section_id: string;
      value: string;
      test_node_input_ids: Set<string>;
    }
  >;
};

export type FormGroupSerialized = {
  form_node_id: string;
  form_id: string;
  form_name: string;
  fields: {
    field: {
      id: string;
      name: string;
      type: string;
      label?: string;
      form_step_id?: string;
      form_section_id?: string;
    };
    step_id: string;
    step_name: string;
    section_id: string;
    value: string;
    test_node_input_ids: Set<string>;
  }[];
};

export type SimpleNodeSerialized = {
  node_id: string;
  node_name: string;
  node_type: string;
  value: string;
  test_node_input_ids: Set<string>;
};

const wrapSimpleNodes = (
  conditions: Conditions,
  variables: Node[],
): SimpleNodeSerialized[] => {
  const simpleNodes = new Map<string, SimpleNodeSerialized>();
  const processInput = (input: Input[number]) => {
    if (!input.test_node_input) return;
    const sourceConnection = input.source_connections?.find((sc) => {
      return sc.target_node_input_id === input.id;
    });
    if (!sourceConnection) return;
    const sourceNodeId = sourceConnection.source_node_id;
    const formNode = variables.find((node) => node.id === sourceNodeId);
    if (!formNode) return;
    const nodeData = formNode.data as unknown as NodeData;
    if (nodeData.node_type !== "FORM") {
      if (!simpleNodes.has(sourceNodeId)) {
        simpleNodes.set(sourceNodeId, {
          node_id: sourceNodeId,
          node_name: nodeData.displayName || "",
          node_type: nodeData.type,
          value: (input?.test_node_input?.value as string) || "",
          test_node_input_ids: new Set([input.test_node_input.id || ""]),
        });
      } else {
        simpleNodes
          .get(sourceNodeId)!
          .test_node_input_ids.add(input.test_node_input.id);
      }
    }
  };
  conditions.forEach((c) => {
    if (c.input) {
      processInput(c.input);
    }
    if (c.optional_input) {
      processInput(c.optional_input);
    }
  });
  return Array.from(simpleNodes.values());
};

const wrapConditions = (
  conditions: Conditions,
  variables: Node[],
): FormGroupSerialized[] => {
  const formGroups = new Map<string, FormGroup>();

  const processInput = (input: Input[number]) => {
    if (!input.test_node_input) return;

    const sourceConnection = input.source_connections?.find((sc) => {
      return sc.target_node_input_id === input.id;
    });

    if (!sourceConnection) return;

    const sourceNodeId = sourceConnection.source_node_id;
    const fieldId =
      sourceConnection?.field?.id || sourceConnection.source_node_id;
    if (!formGroups.has(sourceNodeId)) {
      const formNode = variables.find((node) => node.id === sourceNodeId);
      if (!formNode) return;
      const nodeData = formNode.data as unknown as NodeData;
      const formInput = nodeData.inputs.find((i) => i.type === "FORM");
      if (!formInput) return;

      const formId = formInput?.value as string;
      formGroups.set(sourceNodeId, {
        form_node_id: sourceNodeId,
        form_id: formId,
        form_name: nodeData.displayName || "",
        fields: new Map(),
      });
    }

    const formGroup = formGroups.get(sourceNodeId)!;
    if (!formGroup.fields.has(fieldId)) {
      formGroup.fields.set(fieldId, {
        field: sourceConnection.field,
        step_id: sourceConnection.field.form_step_id || "",
        step_name: "",
        value: (input?.test_node_input?.value as string) || "",
        section_id: sourceConnection.field.form_section_id || "",
        test_node_input_ids: new Set([input.test_node_input.id || ""]),
      });
    } else {
      formGroup.fields
        .get(fieldId)!
        .test_node_input_ids.add(input.test_node_input.id);
    }
  };

  conditions.forEach((c) => {
    if (c.input) {
      processInput(c.input);
    }
    if (c.optional_input) {
      processInput(c.optional_input);
    }
  });

  return Array.from(formGroups.values()).map((fg) => {
    return {
      form_node_id: fg.form_node_id,
      form_id: fg.form_id,
      form_name: fg.form_name,
      fields: Array.from(fg.fields.entries()).map(([fieldId, fieldData]) => {
        return {
          field_id: fieldId,
          ...fieldData,
        };
      }),
    };
  });
};

const Wrapper = () => {
  const { modalState, setModalState, selectedNodeId, setSelectedNodeId } =
    useUIStore();
  const { variables: nodeVariables, isLoading } = useGetVariables();
  const { getNodeById, getNodesVariabes } = useWorkflowStore();
  const variables = getNodesVariabes(selectedNodeId || "");

  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  const { mutateAsync, isPending } = useUpdateNodeTestInput();
  const { mutateAsync: executeNode, isPending: isLoadingExecution } =
    useExecuteNodeInSandbox();

  const {
    data,
    refetch,
    isPending: isFetchingResults,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetNodeTestInputExecution(selectedNodeId!);

  const methods = useForm<{
    data: FormGroupSerialized[];
    simple_nodes: SimpleNodeSerialized[];
  }>({
    defaultValues: {
      data: wrapConditions(nodeData.inputs[0].conditions || [], variables),
      simple_nodes: wrapSimpleNodes(
        nodeData.inputs[0].conditions || [],
        variables,
      ),
    },
  });

  const onSave = async () => {
    const formData = methods.getValues();
    console.log(formData);
    const body = formData.data
      .map(({ fields }) => {
        return fields.map((f) => {
          return Array.from(f.test_node_input_ids).map((id) => {
            return {
              id,
              value: f.value || "",
            };
          });
        });
      })
      .flatMap((i) => i)
      .flatMap((i) => i);
    try {
      await mutateAsync(body);
      await executeNode(selectedNodeId || "");
      setTimeout(() => {
        refetch();
      }, 500);
    } catch {
      console.log("");
    }
  };

  return (
    <FullModal
      isOpen={modalState === "test-input"}
      isSaveValid
      //isSaveValid={!isAddConditionDisabled}
      onClose={() => {
        setSelectedNodeId();
        setModalState("idle");
        //setLocalModalState("close-warning");
      }}
      onSave={onSave}
      isLoading={isPending || isLoadingExecution || isFetchingResults}
      actionLabel="Probar"
    >
      <ThreeColumnLayout
        className="overflow-auto"
        leftHeader={{
          icon: "arrow-right-to-bracket",
          title: "Inputs",
          color: "neutral",
        }}
        centerHeader={{
          icon: "split",
          title: nodeData.displayName || "",
          color: "neutral",
          editable: false,
          onTitleChange: () => {},
        }}
        rightHeader={{
          icon: "arrow-right-from-bracket",
          title: "Outputs",
          color: "neutral",
        }}
        leftContent={
          <FormProvider {...methods}>
            <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
              <VariableInput />
              {/* <Variables sourceNodeId={selectedNodeId!} /> */}
            </div>
          </FormProvider>
        }
        disableContent
        centerContent={
          !isLoading ? (
            <Content variables={nodeVariables} />
          ) : (
            <div>
              <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="animate-spin text-xl text-body"
                />
              </p>
            </div>
          )
        }
        rightContent={
          <>
            <Output
              data={
                data?.pages
                  ?.flatMap((p) => {
                    console.log(p);
                    return p.data;
                  })
                  .flatMap((d) => d.outputs) || []
              }
              loadMore={fetchNextPage}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              isLoading={isFetchingResults}
            />
          </>
        }
      />
    </FullModal>
  );
};

const IfTestInput = () => {
  const { getNodeById } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  return (
    <ConditionProvider inputs={nodeData.inputs || []}>
      <Wrapper />
    </ConditionProvider>
  );
};

export default IfTestInput;
